import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
  mergeClasses,
  Radio,
  RadioGroup,
} from '@fluentui/react-components';

interface PartySelectPromptProps {
  open: boolean;
  parties: string[];
  /** Resolves with the chosen party, or null if the user cancelled. */
  onResolve: (party: string | null) => void;
}

const SIGNUS_BLUE = '#1014f5';
const SIGNUS_BLUE_HOVER = '#2a2eff';
const SIGNUS_BLUE_PRESSED = '#0a0dcc';

const useStyles = makeStyles({
  content: {
    fontSize: '14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  button: {
    minWidth: 'auto',
    paddingLeft: '16px',
    paddingRight: '16px',
    borderRadius: '8px',
    whiteSpace: 'nowrap',
    '& *': { whiteSpace: 'nowrap' },
  },
  secondaryText: {
    color: SIGNUS_BLUE,
    ':hover': { color: SIGNUS_BLUE_HOVER },
    ':hover:active': { color: SIGNUS_BLUE_PRESSED },
  },
  primary: {
    backgroundColor: SIGNUS_BLUE,
    border: `1px solid ${SIGNUS_BLUE}`,
    color: '#ffffff',
    ':hover': {
      backgroundColor: SIGNUS_BLUE_HOVER,
      border: `1px solid ${SIGNUS_BLUE_HOVER}`,
      color: '#ffffff',
    },
    ':hover:active': {
      backgroundColor: SIGNUS_BLUE_PRESSED,
      border: `1px solid ${SIGNUS_BLUE_PRESSED}`,
      color: '#ffffff',
    },
  },
});

const PartySelectPrompt: React.FC<PartySelectPromptProps> = ({ open, parties, onResolve }) => {
  const styles = useStyles();
  const [selected, setSelected] = useState<string>('');

  useEffect(() => {
    if (open) setSelected(parties[0] ?? '');
  }, [open, parties]);

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open) onResolve(null);
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Which party are you?</DialogTitle>
          <DialogContent className={styles.content}>
            <span>
              We detected the following parties in this document. Pick the one you represent so
              redlines can be shifted in your favor.
            </span>
            <RadioGroup value={selected} onChange={(_, data) => setSelected(data.value)}>
              {parties.map(party => (
                <Radio key={party} value={party} label={party} />
              ))}
            </RadioGroup>
          </DialogContent>
          <DialogActions>
            <Button
              className={mergeClasses(styles.button, styles.secondaryText)}
              onClick={() => onResolve(null)}
            >
              Cancel
            </Button>
            <Button
              className={mergeClasses(styles.button, styles.primary)}
              appearance="primary"
              disabled={!selected}
              onClick={() => onResolve(selected)}
            >
              Continue
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default PartySelectPrompt;
